import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Box,
  Chip,
} from '@mui/material';
import { AutoAwesome as SparkleIcon, History as ClassicIcon } from '@mui/icons-material';
import { ChartLayout } from './useChartLayout';

export interface ChartLayoutToggleProps {
  layout: ChartLayout;
  onChange: (next: ChartLayout) => void;
  variant?: 'header' | 'inline';
}

const COPY: Record<ChartLayout, {
  prompt: string;
  swapTo: ChartLayout;
  swapLabel: string;
  swapIcon: React.ReactElement;
  confirmTitle: string;
  confirmBody: React.ReactNode;
  confirmCta: string;
}> = {
  classic: {
    prompt: 'Try modern',
    swapTo: 'modern',
    swapLabel: 'Try modern',
    swapIcon: <SparkleIcon sx={{ fontSize: 16 }} />,
    confirmTitle: 'Switch to the modern chart?',
    confirmBody: (
      <>
        The modern chart keeps everything you already use, with a few additions:
        <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 1 }}>
          <li>One-click resume of unfinished SOAP drafts</li>
          <li>Always-visible alerts &amp; active meds (left rail)</li>
          <li>Click any history row to read it without leaving the page</li>
        </Box>
        You can switch back any time from the header.
      </>
    ),
    confirmCta: 'Try the modern chart',
  },
  modern: {
    prompt: 'Switch to classic',
    swapTo: 'classic',
    swapLabel: 'Classic view',
    swapIcon: <ClassicIcon sx={{ fontSize: 16 }} />,
    confirmTitle: 'Switch back to the classic chart?',
    confirmBody: (
      <>
        The classic chart is the familiar tab-based layout — Visit Notes, SOAP, Labs, Medications,
        and so on as separate tabs above the chart.
        <Box sx={{ mt: 1 }}>
          Your data and unfinished drafts are unaffected; only the layout changes.
        </Box>
      </>
    ),
    confirmCta: 'Use the classic chart',
  },
};

/**
 * Small toggle pill that lets a user switch between the classic and modern
 * chart layouts. Tapping opens a brief confirmation so the swap isn't an
 * accident — the layout change is significant enough that we want a moment
 * of intent. All state lives in `useChartLayout`; this component is dumb.
 */
const ChartLayoutToggle: React.FC<ChartLayoutToggleProps> = ({ layout, onChange, variant = 'header' }) => {
  const [open, setOpen] = useState(false);
  const copy = COPY[layout];

  const confirm = () => {
    onChange(copy.swapTo);
    setOpen(false);
  };

  return (
    <>
      {variant === 'header' ? (
        <Chip
          icon={copy.swapIcon}
          label={copy.swapLabel}
          size="small"
          onClick={() => setOpen(true)}
          data-testid="chart-layout-toggle"
          sx={{
            height: 26,
            fontSize: '0.72rem',
            fontWeight: 600,
            bgcolor: layout === 'classic' ? 'rgba(201,119,43,0.12)' : 'rgba(139,94,52,0.08)',
            color: layout === 'classic' ? '#C9772B' : '#5D4037',
            border: '1px solid',
            borderColor: layout === 'classic' ? 'rgba(201,119,43,0.35)' : 'rgba(139,94,52,0.2)',
            cursor: 'pointer',
            '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
            '&:hover': {
              bgcolor: layout === 'classic' ? 'rgba(201,119,43,0.2)' : 'rgba(139,94,52,0.14)',
              borderColor: '#C9772B',
            },
          }}
        />
      ) : (
        <Button
          size="small"
          variant="text"
          startIcon={copy.swapIcon}
          onClick={() => setOpen(true)}
          data-testid="chart-layout-toggle"
          sx={{ textTransform: 'none', fontSize: '0.78rem' }}
        >
          {copy.swapLabel}
        </Button>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{copy.confirmTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="div" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {copy.confirmBody}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {copy.prompt}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setOpen(false)} size="small" sx={{ textTransform: 'none' }}>
                Not now
              </Button>
              <Button onClick={confirm} variant="contained" size="small" sx={{ textTransform: 'none' }}>
                {copy.confirmCta}
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChartLayoutToggle;
